import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import '../styles/about.scss';

import Star from '../assets/images/star.png';

import CountUp from './micro/CountUp';
import logo from '../assets/images/logowhite.png';
import ShinyText from './micro/Shinytext';

gsap.registerPlugin(ScrollTrigger);

// SVG Icons for values
const Icons = {
  clarity: () => (
    <svg viewBox="0 0 48 48" fill="none">
      <circle cx="24" cy="24" r="20" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="24" cy="24" r="8" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  ),
  performance: () => (
    <svg viewBox="0 0 48 48" fill="none">
      <path d="M4 36L16 24L24 32L44 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M32 12H44V24" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  partnership: () => (
    <svg viewBox="0 0 48 48" fill="none">
      <circle cx="16" cy="20" r="8" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="32" cy="20" r="8" stroke="currentColor" strokeWidth="1.5" />
      <path d="M8 40C8 33.4 13.4 28 20 28H28C34.6 28 40 33.4 40 40" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  longterm: () => (
    <svg viewBox="0 0 48 48" fill="none">
      <rect x="8" y="8" width="32" height="32" rx="4" stroke="currentColor" strokeWidth="1.5" />
      <path d="M16 24H32" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M16 18H28" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M16 30H26" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
};

const About = () => {
  const { t, i18n } = useTranslation();
  const heroRef = useRef(null);
  const storyRef = useRef(null);
  const valuesRef = useRef(null);
  const statsRef = useRef(null);
  const processRef = useRef(null);

  const stats = [
    { value: 100, suffix: t('about.stat1_suffix'), label: t('about.stat1_label') },
    { value: 4, suffix: t('about.stat2_suffix'), label: t('about.stat2_label') },
    { value: 24, suffix: t('about.stat3_suffix'), label: t('about.stat3_label') },
  ];

  const values = [
    {
      icon: 'clarity',
      title: t('about.value1_title'),
      description: t('about.value1_desc')
    },
    {
      icon: 'performance',
      title: t('about.value2_title'),
      description: t('about.value2_desc')
    },
    {
      icon: 'partnership',
      title: t('about.value3_title'),
      description: t('about.value3_desc')
    },
    {
      icon: 'longterm',
      title: t('about.value4_title'),
      description: t('about.value4_desc')
    }
  ];

  const process = [
    { step: '01', title: t('about.process1_title'), description: t('about.process1_desc') },
    { step: '02', title: t('about.process2_title'), description: t('about.process2_desc') },
    { step: '03', title: t('about.process3_title'), description: t('about.process3_desc') },
    { step: '04', title: t('about.process4_title'), description: t('about.process4_desc') },
  ];

  // Refresh ScrollTrigger when language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      setTimeout(() => {
        ScrollTrigger.refresh();
      }, 100);
    };

    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      if (heroRef.current) {
        gsap.fromTo(
          heroRef.current.querySelectorAll('.reveal'),
          { opacity: 0, y: 40 },
          { opacity: 1, y: 0, duration: 0.7, stagger: 0.1, ease: 'power3.out', delay: 0.2 }
        );
      }

      if (storyRef.current) {
        gsap.fromTo(
          storyRef.current.querySelectorAll('.reveal'),
          { opacity: 0, y: 30 },
          {
            opacity: 1, y: 0, duration: 0.6, stagger: 0.08, ease: 'power3.out',
            scrollTrigger: { trigger: storyRef.current, start: 'top 75%' }
          }
        );
      }

      if (valuesRef.current) {
        gsap.fromTo(
          valuesRef.current.querySelectorAll('.about__value'),
          { opacity: 0, y: 30 },
          {
            opacity: 1, y: 0, duration: 0.5, stagger: 0.1, ease: 'power3.out',
            scrollTrigger: { trigger: valuesRef.current, start: 'top 75%' }
          }
        );
      }

      if (statsRef.current) {
        gsap.fromTo(
          statsRef.current.querySelectorAll('.about__stat'),
          { opacity: 0, y: 20 },
          {
            opacity: 1, y: 0, duration: 0.5, stagger: 0.08, ease: 'power3.out',
            scrollTrigger: { trigger: statsRef.current, start: 'top 80%' }
          }
        );
      }

      if (processRef.current) {
        gsap.fromTo(
          processRef.current.querySelectorAll('.about__process-step'),
          { opacity: 0, y: 30 },
          {
            opacity: 1, y: 0, duration: 0.5, stagger: 0.12, ease: 'power3.out',
            scrollTrigger: { trigger: processRef.current, start: 'top 75%' }
          }
        );
      }
    });

    return () => ctx.revert();
  }, []);

  return (
    <div className="about">
      {/* Hero */}
      <section className="about__hero" ref={heroRef}>
        <div className="section-shell">
          <span className="about__label reveal">{t('about.label')}</span>
          <h1 className="reveal">
            {t('about.hero_title')}
          </h1>
          <p className="about__intro reveal">
            {t('about.hero_intro')}
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="about__stats" ref={statsRef}>
        <div className="section-shell">
          <div className="about__stats-grid">
            {stats.map((stat, index) => (
              <div className="about__stat" key={index}>
                <div className="about__stat-value">
                  <CountUp from={0} to={stat.value} duration={1} separator="" />
                  <span>{stat.suffix}</span>
                </div>
                <ShinyText text={stat.label} speed={3} className="about__stat-label" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="about__story" ref={storyRef}>
        <div className="section-shell">
          <div className="about__story-grid">
            <div className="about__story-left">
              <span className="about__label reveal">{t('about.story_label')}</span>
              <h2 className="reveal">{t('about.story_title')}</h2>
            </div>
            <div className="about__story-right">
              <p className="reveal">
                {t('about.story_p1')}
              </p>
              <p className="reveal">
                {t('about.story_p2')}
              </p>
              <p className="reveal">
                {t('about.story_p3')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="about__values" ref={valuesRef}>
        <div className="section-shell">
          <div className="about__values-header">
            <span className="about__label">{t('about.approach_label')}</span>
            <h2>{t('about.approach_title')}</h2>
          </div>
          <div className="about__values-grid">
            {values.map((value, index) => {
              const IconComponent = Icons[value.icon];
              return (
                <div className="about__value" key={index}>
                  <div className="about__value-icon">
                    <IconComponent />
                  </div>
                  <h3>{value.title}</h3>
                  <p>{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="about__process" ref={processRef}>
        <div className="section-shell">
          <div className="about__process-header">
            <span className="about__label">{t('about.process_label')}</span>
            <h2>{t('about.process_title')}</h2>
          </div>
          <div className="about__process-grid">
            {process.map((item, index) => (
              <div className="about__process-step" key={index}>
                <span className="about__process-number">{item.step}</span>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="about__cta">
        <div className="section-shell">
          <div className="about__cta-content">
            <img src={logo} alt="Yves Web Design" className="about__cta-logo" />
            <h2>{t('about.cta_title')}</h2>
            <p>{t('about.cta_desc')}</p>
            <a href="/contact" className="about__cta-btn">
            <span className="btn__icon">
                <img src={Star} alt="" />
              </span>
              <span>{t('about.cta_button')}</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
